import { createContext, useCallback, useContext, useRef, useState, ReactNode } from "react";

// A large pool of distinct animated background effects. One fires every time
// a color is selected, chosen at random (never the same effect twice in a
// row), so the "after you choose" moment always looks different.
export type PulseEffect =
  | 'burst' | 'sweep' | 'ripple' | 'orbs' | 'wave' | 'spiral' | 'vortex'
  | 'kaleidoscope' | 'aurora' | 'plasma' | 'bokeh' | 'starfield'
  | 'rain' | 'confetti' | 'comet' | 'fireflies';

const EFFECTS: PulseEffect[] = [
  'burst', 'sweep', 'ripple', 'orbs', 'wave', 'spiral', 'vortex',
  'kaleidoscope', 'aurora', 'plasma', 'bokeh', 'starfield',
  'rain', 'confetti', 'comet', 'fireflies',
];

export interface PulseState {
  hex: string;
  effect: PulseEffect;
  key: number;
}

interface ColorPulseContextValue {
  pulse: PulseState | null;
  trigger: (hex: string) => void;
  optionColors: string[];
  setOptionColors: (hexes: string[]) => void;
}

const ColorPulseContext = createContext<ColorPulseContextValue | null>(null);

export function ColorPulseProvider({ children }: { children: ReactNode }) {
  const [pulse, setPulse] = useState<PulseState | null>(null);
  const [optionColors, setOptionColors] = useState<string[]>([]);
  const lastEffect = useRef<PulseEffect | null>(null);
  const counter = useRef(0);

  const trigger = useCallback((hex: string) => {
    let effect = EFFECTS[Math.floor(Math.random() * EFFECTS.length)];
    // Never let the same effect fire twice in a row so every selection feels distinct.
    if (effect === lastEffect.current) {
      effect = EFFECTS[(EFFECTS.indexOf(effect) + 1) % EFFECTS.length];
    }
    lastEffect.current = effect;
    counter.current += 1;
    setPulse({ hex, effect, key: counter.current });
  }, []);

  return (
    <ColorPulseContext.Provider value={{ pulse, trigger, optionColors, setOptionColors }}>
      {children}
    </ColorPulseContext.Provider>
  );
}

export function useColorPulse() {
  const ctx = useContext(ColorPulseContext);
  if (!ctx) throw new Error("useColorPulse must be used within a ColorPulseProvider");
  return ctx;
}
